// This project has no tsconfig.json, so this reference is needed to pull in
// react-app-env.d.ts's ambient declarations (react-scripts types, *.css
// wildcard module) — an unimported .d.ts otherwise isn't part of this file's
// inferred TS project.
/// <reference path="../../../../../react-app-env.d.ts" />
import React, { useState, useRef } from 'react';
import { useRestaurant } from '../../contexts/RestaurantContext.tsx';
import { Square, Circle, X } from 'lucide-react';
import { toast } from 'react-toastify';
import '../../styles/element-creator.css';
import { calculateTableDimensions } from '../../constants.ts';

interface ElementCreatorProps {
  onClose: () => void;
}

const ElementCreator: React.FC<ElementCreatorProps> = ({ onClose }) => {
  const { addTable, addElement } = useRestaurant();
  
  const [elementType, setElementType] = useState<'table' | 'object' | 'door'>('table');
  const [name, setName] = useState('');
  const [shape, setShape] = useState<'square' | 'rectangle' | 'circle'>('square');
  const [texture, setTexture] = useState('light-wood');
  const [minCovers, setMinCovers] = useState(2);
  const [maxCovers, setMaxCovers] = useState(4);
  const [textColor, setTextColor] = useState('#000000');
  const [chairColor, setChairColor] = useState('#8B4513');
  const [elementColor, setElementColor] = useState('#e9c46a');
  const [tableColor, setTableColor] = useState('#8B4513');
  const [useColor, setUseColor] = useState(false);
  const [error, setError] = useState('');
  const nameInputRef = useRef<HTMLInputElement>(null);
  
  const handleCreate = async () => {
    if (!name.trim()) {
      setError('Nome é obrigatório');
      nameInputRef.current?.focus();
      return;
    }
    setError('');
    
    try {
      if (elementType === 'table') {
        // Determine initial size in pixels based on real-world proportions
        let { width, height } = calculateTableDimensions(shape, maxCovers);

        // Ensure the size respects the minimum allowed by the table component
        if (width < 60) width = 60;
        if (height < 60) height = 60;

        const created = await addTable({
          name,
          shape: { type: shape, width, height },
          texture,
          position: { x: 100, y: 100 },
          minCovers,
          maxCovers,
          rotation: 0,
          textColor,
          chairColor,
          color: tableColor,
          useColor
        });

        if (!created) {
          toast.error('Não foi possível criar a mesa.');
          return;
        }
        toast.success('Mesa criada com sucesso.');
      } else {
        // Create a new element (door or object)
        const elementSize = elementType === 'door'
          ? { width: 60, height: 20 }
          : { width: shape === 'rectangle' ? 120 : 80, height: shape === 'rectangle' ? 60 : 80 };

        await addElement({
          type: elementType,
          name,
          shape: elementType === 'door' ? 'rectangle' : shape,
          position: { x: 100, y: 100 },
          ...elementSize,
          rotation: 0,
          textColor,
          color: elementType === 'object' ? elementColor : undefined
        });
      }

      onClose();
    } catch (createError) {
      console.error('Erro ao criar elemento no layout:', createError);
      toast.error('Não foi possível criar o elemento.');
    }
  };
  
  return (
    <div className="element-creator-overlay">
      <div className="element-creator">
        <div className="creator-header">
          <h3>Adicionar Novo Elemento</h3>
          <button className="close-btn" onClick={onClose}>
            <X size={18} />
          </button>
        </div>
        
        <div className="creator-form">
          <div className="form-group">
            <label>Tipo do Elemento</label>
            <div className="element-type-selector">
              <button
                className={`type-btn ${elementType === 'table' ? 'active' : ''}`}
                onClick={() => setElementType('table')}
              >
                <Square size={28} />
                <span>Mesa</span>
              </button>
              <button
                className={`type-btn ${elementType === 'object' ? 'active' : ''}`}
                onClick={() => setElementType('object')}
              >
                <Circle size={28} />
                <span>Objeto</span>
              </button>
            </div>
          </div>
          
          <div className="form-group">
            <label htmlFor="element-name">Nome</label>
            <input
              id="element-name"
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder={elementType === 'table' ? 'T1, Mesa 1, etc.' : 'Palco, Bar, etc.'}
              className="form-control"
              ref={nameInputRef}
            />
          </div>

          {error && <div style={{ color: 'red', marginTop: '5px' }}>{error}</div>}

          {elementType === 'object' && (
            <>
              <div className="form-group">
                <label htmlFor="element-color">Cor do Elemento</label>
                <div className="color-input">
                  <input
                    id="element-color"
                    type="color"
                    value={elementColor}
                    onChange={(e) => setElementColor(e.target.value)}
                    className="form-control"
                  />
                </div>
              </div>

              <div className="form-group">
                <label htmlFor="text-color">Cor do Texto</label>
                <div className="color-input">
                  <input
                    id="text-color"
                    type="color"
                    value={textColor}
                    onChange={(e) => setTextColor(e.target.value)}
                    className="form-control"
                  />
                </div>
              </div>
            </>
          )}
          
          {elementType !== 'door' && (
            <div className="form-group">
              <label>Formato</label>
              <div className="shape-selector">
                <button 
                  className={`shape-btn ${shape === 'square' ? 'active' : ''}`}
                  onClick={() => setShape('square')}
                >
                  <Square size={28} />
                </button>
                <button 
                  className={`shape-btn ${shape === 'circle' ? 'active' : ''}`}
                  onClick={() => setShape('circle')}
                >
                  <Circle size={28} />
                </button>
              </div>
            </div>
          )}
          
          {elementType === 'table' && (
            <>
              <div className="form-group">
                <label>Aparência da Mesa</label>
                <div className="appearance-selector">
                  <button
                    className={`appearance-btn ${!useColor ? 'active' : ''}`}
                    onClick={() => setUseColor(false)}
                  >
                    Textura
                  </button>
                  <button
                    className={`appearance-btn ${useColor ? 'active' : ''}`}
                    onClick={() => setUseColor(true)}
                  >
                    Cor
                  </button>
                </div>
              </div>

              {!useColor ? (
                <div className="form-group">
                  <label>Textura</label>
                  <div className="texture-selector">
                    <button
                      className={`texture-btn ${texture === 'light-wood' ? 'active' : ''}`}
                      onClick={() => setTexture('light-wood')}
                      style={{ backgroundImage: 'url(/textures/light-wood.jpg)' }}
                    ></button>
                    <button
                      className={`texture-btn ${texture === 'dark-wood' ? 'active' : ''}`}
                      onClick={() => setTexture('dark-wood')}
                      style={{ backgroundImage: 'url(/textures/dark-wood.jpg)' }}
                    ></button>
                    <button
                      className={`texture-btn ${texture === 'black' ? 'active' : ''}`}
                      onClick={() => setTexture('black')}
                      style={{ backgroundColor: '#222' }}
                    ></button>
                  </div>
                </div>
              ) : (
                <div className="form-group">
                  <label htmlFor="table-color-create">Cor da Mesa</label>
                  <div className="color-input">
                    <input
                      id="table-color-create"
                      type="color"
                      value={tableColor}
                      onChange={(e) => setTableColor(e.target.value)}
                      className="form-control"
                    />
                  </div>
                </div>
              )}
              
              <div className="form-group">
                <label htmlFor="min-covers">Mín. Lugares</label>
                <div className="number-input">
                  <input
                    id="min-covers"
                    type="number"
                    min="1"
                    max={maxCovers}
                    value={minCovers}
                    onChange={(e) => setMinCovers(Math.min(parseInt(e.target.value), maxCovers))}
                    className="form-control"
                  />
                </div>
              </div>
              
              <div className="form-group">
                <label htmlFor="max-covers">Máx. Lugares</label>
                <div className="number-input">
                  <input
                    id="max-covers"
                    type="number"
                    min={minCovers}
                    max="20"
                    value={maxCovers}
                    onChange={(e) => setMaxCovers(Math.max(minCovers, parseInt(e.target.value)))}
                    className="form-control"
                  />
                </div>
              </div>

              <div className="form-group">
                <label htmlFor="table-text-color">Cor do Texto</label>
                <div className="color-input">
                  <input
                    id="table-text-color"
                    type="color"
                    value={textColor}
                    onChange={(e) => setTextColor(e.target.value)}
                    className="form-control"
                  />
                </div>
              </div>

              <div className="form-group">
                <label htmlFor="table-chair-color">Cor das Cadeiras</label>
                <div className="color-input">
                  <input
                    id="table-chair-color"
                    type="color"
                    value={chairColor}
                    onChange={(e) => setChairColor(e.target.value)}
                    className="form-control"
                  />
                </div>
              </div>
            </>
          )}
          
          <button className="btn-create" onClick={handleCreate}>
            Criar
          </button>
        </div>
      </div>
    </div>
  );
};

export default ElementCreator;
